import { MaximilianaTitle } from '@mxa-argos/components/custom/maximiliana-title';
import { SidebarProfileMenu } from '@mxa-argos/components/custom/sidebar-profile-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from '@mxa-argos/components/shadcn/sidebar';
import { useSidebar } from '@mxa-argos/components/shadcn/use-sidebar';
import { cn } from '@mxa-argos/lib/utils';
import { useForjaRunning } from '@mxa-argos/modules/forja/hooks/use-forja-running';
import {
  Anvil,
  ArrowRightLeft,
  Blocks,
  CardSim,
  FileStack,
  Headset,
  LayoutDashboard,
  Package,
  RefreshCw,
  Replace,
  Search,
  ShieldAlert,
  Smartphone,
  Truck,
  UserCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

const desktopNavGroups = [
  [
    { titleKey: 'nav.customers', url: '/customers', icon: UserCircle },
    { titleKey: 'nav.subscriptions', url: '/subscriptions', icon: FileStack },
    { titleKey: 'nav.portabilities', url: '/portabilities', icon: ArrowRightLeft },
  ],
  [
    { titleKey: 'nav.orders', url: '/orders', icon: Package },
    { titleKey: 'nav.changes', url: '/changes', icon: Replace },
    { titleKey: 'nav.shipments', url: '/shipments', icon: Truck },
    { titleKey: 'nav.forja', url: '/forja', icon: Anvil },
  ],
  [
    { titleKey: 'nav.devices', url: '/devices', icon: Smartphone },
    { titleKey: 'nav.sims', url: '/sims', icon: CardSim },
  ],
  [
    { titleKey: 'nav.fraudProfiles', url: '/fraud-profiles', icon: ShieldAlert },
    { titleKey: 'nav.externalApps', url: '/external-apps', icon: Blocks },
    { titleKey: 'nav.supportAccount', url: '/support', icon: Headset },
    { titleKey: 'nav.syncMode', url: '/sync', icon: RefreshCw },
  ],
];

const mobileNavItems = [
  { titleKey: 'nav.dashboard', url: '/', icon: LayoutDashboard },
  { titleKey: 'nav.search', url: '/search', icon: Search },
];

const SidebarLogo: React.FC = () => {
  const { open } = useSidebar();

  if (!open) {
    return (
      <SidebarHeader className="flex items-center justify-center p-2 pt-4">
        <Link to="/">
          <img src="/iso.svg" alt="Maximiliana" className="size-8" />
        </Link>
      </SidebarHeader>
    );
  }

  return (
    <SidebarHeader className="p-4">
      <MaximilianaTitle />
    </SidebarHeader>
  );
};

export const AppSidebar: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useSidebar();
  const location = useLocation();
  const isForjaRunning = useForjaRunning();

  const navGroups = isMobile ? [mobileNavItems] : desktopNavGroups;

  return (
    <Sidebar collapsible="icon">
      <SidebarLogo />

      <SidebarContent>
        {navGroups.map((group, groupIndex) => (
          <SidebarGroup
            key={groupIndex}
            className={cn(groupIndex > 0 && 'pt-0', groupIndex < navGroups.length - 1 && 'pb-0')}
          >
            {groupIndex > 0 && <SidebarSeparator className="mt-1 mb-2" />}
            <SidebarGroupContent>
              <SidebarMenu>
                {group.map((item) => (
                  <SidebarMenuItem key={item.titleKey}>
                    <SidebarMenuButton
                      asChild
                      isActive={location.pathname === item.url}
                      tooltip={t(item.titleKey)}
                    >
                      <Link to={item.url}>
                        <item.icon className="size-4" />
                        <span className="group-data-[collapsible=icon]:hidden">
                          {t(item.titleKey)}
                        </span>
                        {item.url === '/forja' && isForjaRunning && (
                          <span
                            className="ml-auto size-2 shrink-0 animate-pulse rounded-full bg-mxa-green"
                            title={t('forja.running')}
                          />
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="pb-4">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarProfileMenu />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};
